import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiX, FiSearch } from 'react-icons/fi';

const SBFPaymentPage = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalPayments, setTotalPayments] = useState(0);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [currencyFilter, setCurrencyFilter] = useState('all');
    const [sbfMembers, setSbfMembers] = useState([]);
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
    const [showMemberDropdown, setShowMemberDropdown] = useState(false);
    const [formData, setFormData] = useState({
        sbfAdsID: '',
        currency: 'SGD',
        amount: '',
        rate: '1.00',
        paymentDate: '',
        renewalDate: '',
        remarks: ''
    });

    const currencies = ['USDT', 'SGD', 'LTC'];

    // Fetch SBF Members for dropdown
    const fetchSBFMembers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/sbfmembers?limit=1000`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setSbfMembers(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch SBF members:', err);
        }
    };

    useEffect(() => {
        fetchSBFMembers();
    }, []);

    const fetchPayments = useCallback(async (page, search = '', currency = 'all') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/sbfpayments?page=${page}&limit=20&sort=-paymentDate`;
            
            if (search) apiUrl += `&search=${search}`;
            if (currency !== 'all') apiUrl += `&currency=${currency}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setPayments(response.data.data);
                setTotalPages(response.data.pagination.pages);
                setTotalPayments(response.data.pagination.total);
            }
        } catch (err) {
            setError(`Failed to fetch SBF payments: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPayments(currentPage, searchTerm, currencyFilter);
    }, [currentPage, searchTerm, currencyFilter, fetchPayments]);

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const submitData = {
                ...formData,
                amount: parseFloat(formData.amount),
                rate: parseFloat(formData.rate)
            };
            
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/sbfpayments`,
                submitData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            setShowCreateForm(false);
            setFormData({
                sbfAdsID: '',
                currency: 'SGD',
                amount: '',
                rate: '1.00',
                paymentDate: '',
                renewalDate: '',
                remarks: ''
            });
            setMemberSearchTerm('');
            setShowMemberDropdown(false);
            fetchPayments(currentPage, searchTerm, currencyFilter);
        } catch (err) {
            setError(`Failed to create SBF payment: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const submitData = {
                ...formData,
                amount: parseFloat(formData.amount),
                rate: parseFloat(formData.rate)
            };
            
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/sbfpayments/${selectedPayment._id}`,
                submitData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            setShowEditModal(false);
            setSelectedPayment(null);
            setFormData({
                sbfAdsID: '',
                currency: 'SGD',
                amount: '',
                rate: '1.00',
                paymentDate: '',
                renewalDate: '',
                remarks: ''
            });
            setMemberSearchTerm('');
            setShowMemberDropdown(false);
            fetchPayments(currentPage, searchTerm, currencyFilter);
        } catch (err) {
            setError(`Failed to update SBF payment: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleDelete = (payment) => {
        setSelectedPayment(payment);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/sbfpayments/${selectedPayment._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowDeleteConfirm(false);
            setSelectedPayment(null);
            fetchPayments(currentPage, searchTerm, currencyFilter);
        } catch (err) {
            setError(`Failed to delete SBF payment: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleView = (payment) => {
        setSelectedPayment(payment);
        setShowViewModal(true);
    };

    const handleEdit = (payment) => {
        setSelectedPayment(payment);
        setFormData({
            sbfAdsID: payment.sbfAdsID._id,
            currency: payment.currency,
            amount: payment.amount.toString(),
            rate: payment.rate.toString(),
            paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0],
            renewalDate: new Date(payment.renewalDate).toISOString().split('T')[0],
            remarks: payment.remarks || ''
        });
        // Set the member search term for the edit form
        const memberText = `${payment.sbfAdsID.sbfAdsID} - ${payment.sbfAdsID.sbfQuote || 'N/A'} - ${payment.sbfAdsID.sbfAdsName || 'N/A'}`;
        setMemberSearchTerm(memberText);
        setShowEditModal(true);
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setCurrencyFilter('all');
        setCurrentPage(1);
    };

    const handleCurrencyChange = (e) => {
        const currency = e.target.value;
        setFormData(prev => ({
            ...prev,
            currency,
            rate: currency === 'SGD' ? '1.00' : prev.rate
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-SG');
    };

    const formatAmount = (amount, decimals = 2) => {
        if (!amount && amount !== 0) return 'N/A';
        return `$${amount.toFixed(decimals)}`;
    };

    const getCurrencyColor = (currency) => {
        switch (currency) {
            case 'USDT':
                return 'bg-green-100 text-green-800';
            case 'SGD':
                return 'bg-blue-100 text-blue-800';
            case 'LTC':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Filter members based on search term
    const filteredMembers = sbfMembers.filter(member => {
        if (!memberSearchTerm) return true;
        const searchLower = memberSearchTerm.toLowerCase();
        return (
            (member.sbfAdsID && member.sbfAdsID.toLowerCase().includes(searchLower)) ||
            (member.sbfAdsName && member.sbfAdsName.toLowerCase().includes(searchLower)) ||
            (member.sbfQuote && member.sbfQuote.toLowerCase().includes(searchLower))
        );
    });

    const handleMemberSelect = (memberId) => {
        setFormData({ ...formData, sbfAdsID: memberId });
        const member = sbfMembers.find(m => m._id === memberId);
        if (member) {
            setMemberSearchTerm(`${member.sbfAdsID} - ${member.sbfQuote || 'N/A'} - ${member.sbfAdsName || 'N/A'}`);
        }
        setShowMemberDropdown(false);
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">SBF Payments</h1>
                        <p className="text-gray-600">Total: {totalPayments} payments</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create New Payment
                    </button>
                </div>

                {/* Search and Filter Bar */}
                <div className="mb-6">
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                        placeholder="Search by member..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <FiSearch className="h-5 w-5 text-gray-400" />
                                    </div>
                                </div>
                                <button
                                    onClick={handleSearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                                >
                                    <FiSearch className="mr-2" />
                                    Search
                                </button>
                                <button
                                    onClick={handleClear}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div>
                            <select
                                value={currencyFilter}
                                onChange={(e) => {
                                    setCurrencyFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Currencies</option>
                                {currencies.map(curr => (
                                    <option key={curr} value={curr}>{curr}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {/* Create Form Modal */}
                {showCreateForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <h2 className="text-xl font-bold mb-4">Create New SBF Payment</h2>
                            <form onSubmit={handleCreateSubmit}>
                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            SBF Member *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={memberSearchTerm}
                                            onChange={(e) => {
                                                setMemberSearchTerm(e.target.value);
                                                setShowMemberDropdown(true);
                                                if (!e.target.value) {
                                                    setFormData({ ...formData, sbfAdsID: '' });
                                                }
                                            }}
                                            onFocus={() => setShowMemberDropdown(true)}
                                            placeholder="Type to search by ID, Quote, or Name..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        {showMemberDropdown && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                                {filteredMembers.length === 0 ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                        No members found
                                                    </div>
                                                ) : (
                                                    filteredMembers.map(member => (
                                                        <div
                                                            key={member._id}
                                                            onClick={() => handleMemberSelect(member._id)}
                                                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                                        >
                                                            <div className="font-medium text-gray-900">
                                                                {member.sbfAdsID}
                                                            </div>
                                                            <div className="text-gray-600 text-xs">
                                                                {member.sbfQuote || 'N/A'} - {member.sbfAdsName || 'N/A'}
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Currency *
                                        </label>
                                        <select
                                            required
                                            value={formData.currency}
                                            onChange={handleCurrencyChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            {currencies.map(curr => (
                                                <option key={curr} value={curr}>{curr}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            required
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Rate * {formData.currency === 'SGD' && '(Auto-set to 1.00 for SGD)'}
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.rate}
                                            onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                            disabled={formData.currency === 'SGD'}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount in SGD
                                        </label>
                                        <input
                                            type="text"
                                            disabled
                                            value={formData.amount && formData.rate ? (parseFloat(formData.amount) * parseFloat(formData.rate)).toFixed(2) : '0.00'}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-blue-50 text-blue-700 font-bold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Date *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.paymentDate}
                                            onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Renewal Date *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.renewalDate}
                                            onChange={(e) => setFormData({ ...formData, renewalDate: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Remarks
                                        </label>
                                        <textarea
                                            rows="2"
                                            value={formData.remarks}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="Optional remarks..."
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setFormData({
                                                sbfAdsID: '',
                                                currency: 'SGD',
                                                amount: '',
                                                rate: '1.00',
                                                paymentDate: '',
                                                renewalDate: '',
                                                remarks: ''
                                            });
                                            setMemberSearchTerm('');
                                            setShowMemberDropdown(false);
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Create
                                    </button>
                                </div>
                            </form>
                        </div>
                        {/* Backdrop to close dropdown */}
                        {showMemberDropdown && (
                            <div 
                                className="fixed inset-0" 
                                onClick={() => setShowMemberDropdown(false)}
                                style={{ zIndex: -1 }}
                            />
                        )}
                    </div>
                )}

                {/* Edit Form Modal */}
                {showEditModal && selectedPayment && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <h2 className="text-xl font-bold mb-4">Edit SBF Payment</h2>
                            <form onSubmit={handleEditSubmit}>
                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            SBF Member *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={memberSearchTerm}
                                            onChange={(e) => {
                                                setMemberSearchTerm(e.target.value);
                                                setShowMemberDropdown(true);
                                                if (!e.target.value) {
                                                    setFormData({ ...formData, sbfAdsID: '' });
                                                }
                                            }}
                                            onFocus={() => setShowMemberDropdown(true)}
                                            placeholder="Type to search by ID, Quote, or Name..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        {showMemberDropdown && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                                {filteredMembers.length === 0 ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                        No members found
                                                    </div>
                                                ) : (
                                                    filteredMembers.map(member => (
                                                        <div
                                                            key={member._id}
                                                            onClick={() => handleMemberSelect(member._id)}
                                                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                                        >
                                                            <div className="font-medium text-gray-900">
                                                                {member.sbfAdsID}
                                                            </div>
                                                            <div className="text-gray-600 text-xs">
                                                                {member.sbfQuote || 'N/A'} - {member.sbfAdsName || 'N/A'}
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Currency *
                                        </label>
                                        <select
                                            required
                                            value={formData.currency}
                                            onChange={handleCurrencyChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            {currencies.map(curr => (
                                                <option key={curr} value={curr}>{curr}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            required
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Rate * {formData.currency === 'SGD' && '(Auto-set to 1.00 for SGD)'}
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.rate}
                                            onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                            disabled={formData.currency === 'SGD'}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Date *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.paymentDate}
                                            onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Renewal Date *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.renewalDate}
                                            onChange={(e) => setFormData({ ...formData, renewalDate: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Remarks
                                        </label>
                                        <textarea
                                            rows="2"
                                            value={formData.remarks}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="Optional remarks..."
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditModal(false);
                                            setSelectedPayment(null);
                                            setFormData({
                                                sbfAdsID: '',
                                                currency: 'SGD',
                                                amount: '',
                                                rate: '1.00',
                                                paymentDate: '',
                                                renewalDate: '',
                                                remarks: ''
                                            });
                                            setMemberSearchTerm('');
                                            setShowMemberDropdown(false);
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Update
                                    </button>
                                </div>
                            </form>
                        </div>
                        {/* Backdrop to close dropdown */}
                        {showMemberDropdown && (
                            <div 
                                className="fixed inset-0" 
                                onClick={() => setShowMemberDropdown(false)}
                                style={{ zIndex: -1 }}
                            />
                        )}
                    </div>
                )}

                {/* View Modal */}
                {showViewModal && selectedPayment && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold">SBF Payment Details</h2>
                                <button onClick={() => { setShowViewModal(false); setSelectedPayment(null); }}>
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <label className="font-semibold">SBF Member:</label>
                                    <p className="text-gray-700">
                                        {selectedPayment.sbfAdsID?.sbfAdsID} - {selectedPayment.sbfAdsID?.sbfAdsName}
                                    </p>
                                </div>
                                <div>
                                    <label className="font-semibold">Currency:</label>
                                    <span className={`ml-2 px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getCurrencyColor(selectedPayment.currency)}`}>
                                        {selectedPayment.currency}
                                    </span>
                                </div>
                                <div>
                                    <label className="font-semibold">Amount:</label>
                                    <p className="text-gray-700">{formatAmount(selectedPayment.amount, 3)} {selectedPayment.currency}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Rate:</label>
                                    <p className="text-gray-700">{selectedPayment.rate}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Amount (SGD):</label>
                                    <p className="text-gray-700">{formatAmount(selectedPayment.amountSGD)} SGD</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Payment Date:</label>
                                    <p className="text-gray-700">{formatDate(selectedPayment.paymentDate)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Renewal Date:</label>
                                    <p className="text-gray-700">{formatDate(selectedPayment.renewalDate)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Remarks:</label>
                                    <p className="text-gray-700">{selectedPayment.remarks || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Created:</label>
                                    <p className="text-gray-700">{formatDate(selectedPayment.createdAt)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Updated:</label>
                                    <p className="text-gray-700">{formatDate(selectedPayment.updatedAt)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && selectedPayment && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                            <p className="mb-6">Are you sure you want to delete this payment?</p>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => { setShowDeleteConfirm(false); setSelectedPayment(null); }}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    SBF Member
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount (SGD)
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Renewal Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : payments.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center">
                                        No SBF payments found
                                    </td>
                                </tr>
                            ) : (
                                payments.map((payment) => (
                                    <tr key={payment._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm">
                                            <div className="font-medium text-gray-900">{payment.sbfAdsID?.sbfAdsID}</div>
                                            <div className="text-gray-600 text-xs">{payment.sbfAdsID?.sbfQuote || 'N/A'} - {payment.sbfAdsID?.sbfAdsName || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-700">
                                            {formatAmount(payment.amountSGD)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {formatDate(payment.paymentDate)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {formatDate(payment.renewalDate)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button
                                                onClick={() => handleView(payment)}
                                                className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                title="View Details"
                                            >
                                                <FiEye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(payment)}
                                                className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                title="Edit Payment"
                                            >
                                                <FiEdit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(payment)}
                                                className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                title="Delete Payment"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        </Layout>
    );
};

export default SBFPaymentPage;

